
    // IMPORTS
    import { computed, defineComponent, onMounted, ref, h, VNode } from 'vue'
    import { NotificationDTO, useNotificationStore } from '@/store/notification'

    import myGrid from '@/components/grid/my-grid.vue'
    import { lockedOutCell, checkboxCell } from '@/components/grid/cells'
    import { GridCellProps, GridColumnProps } from '@progress/kendo-vue-grid'
    import { Button } from '@progress/kendo-vue-buttons'

    import { GroupDescriptor, SortDescriptor } from '@progress/kendo-data-query'
    import { AccountApi, AuthenticationApi, buildLocalization, Converter, DEBUG_ERR, performButtonAction } from '@/util'
    import { IPermissionGrant, IUserAccountFullDTO } from '@/types/userAccount'
    import { IPartnerAccountFullDTO } from '@/types/partnerAccount'
    import { AxiosError } from 'axios'
    import DateFilterCell from '@/components/grid/cells/date-filter-cell.vue'

    interface IUserRow extends IUserAccountFullDTO {
        accountNo?: number
        companyName?: string
        lastActivity?: Date
        lockedOutUntil?: Date
        language: string
        isPartnerAdmin: boolean
        isPriceAccessAllowed: boolean
    }

    export default defineComponent({
        name: 'adminUserList',

        components: {
            myGrid,
            'k-button': Button
        },

        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const notificationStore = useNotificationStore()

            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            // Original data loaded
            const originalData = ref<Record<string, boolean>>({}) // key -> isPartnerAdmin

            // Dirty rows
            const dirtyRows = ref<Set<string>>(new Set())         // keys that differ from snapshot
            const keyField = 'email' as const
            type TKey = string

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const btnSave = ref<InstanceType<typeof Button> | null>(null)
            const allUsers = ref<IUserRow[] | null>(null)

            const publicData = { btnSave, allUsers }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const initialSort = computed<SortDescriptor[]>(() => [{ field: 'email', dir: 'asc' }])
            const initialGroup = computed<GroupDescriptor[]>(() => [{ field: 'companyName', dir: 'asc' }])
            const anyChanges = computed(() => dirtyRows.value.size > 0)

            const columns = computed(() => {
                {
                    return [
                        {
                            field: 'email',
                            title: localize('Username'),
                            locked: true,
                            width: 300
                        },
                        {
                            field: 'firstName',
                            title: localize('FirstName'),
                        },
                        {
                            field: 'lastName',
                            title: localize('LastName'),
                        },
                        {
                            field: 'language',
                            title: localize('Language'),
                            width: 150
                        },
                        {
                            field: 'accountNo',
                            title: localize('Account'),
                            filter: 'numeric',
                            width: 200
                        },
                        {
                            field: 'companyName',
                            title: localize('Company'),
                        },
                        {
                            field: 'lastActivity',
                            title: localize('LatestActivity'),
                            cell: 'dateTime',
                            filter: 'date',
                            filterCell: DateFilterCell,
                            width: 200
                        },
                        {
                            field: 'isLockedOut',
                            title: localize('LockedOut'),
                            cell: getLockedOutCell,
                            filter: 'boolean',
                            width: 150
                        },
                        {
                            field: 'lockedOutUntil',
                            title: localize('LockedOutUntil'),
                            cell: 'dateTime',
                            filter: 'date',
                            filterCell: DateFilterCell,
                            width: 200
                        },
                        {
                            field: 'isPartnerAdmin',
                            title: localize('IsPartnerAdmin'),
                            cell: getIsPartnerAdminCell,
                            filter: 'boolean',
                            width: 150
                        },
                        {
                            field: 'isPriceAccessAllowed',
                            title: localize('IsPriceAccessAllowed'),
                            filter: 'boolean',
                            cell: 'coloredYesNo',
                            width: 150
                        }
                    ] as GridColumnProps[]
                }
            })

            const computedData = { initialSort, initialGroup, anyChanges, columns }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            async function saveChanges() {
                if (btnSave.value == null || !anyChanges.value || allUsers.value == null) return

                const toSave = allUsers.value.filter(row => dirtyRows.value.has(row[keyField]))
                const permissionGrants: IPermissionGrant[] = toSave.map(row => ({
                    userEmail: row.email,
                    granted: row.isPartnerAdmin
                }))

                let success = true
                const api = new AccountApi
                const target = btnSave.value.$el as HTMLButtonElement
                const action = () => api.setPartnerAdminState(permissionGrants)
                await performButtonAction(target, action)
                    .catch((error: AxiosError | Error) => {
                        DEBUG_ERR(error)
                        const message = localize('UnexpectedError')
                        notificationStore.addNotification(new NotificationDTO(message, 'error'))
                        success = false
                    })


                if (success) {
                    // column "isPriceAccessAllowed" might have changed, reload all users
                    loadUsers()

                    const message = localize('Success_ChangesSaved')
                    notificationStore.addNotification(new NotificationDTO(message, 'success'))
                }
            }

            const publicMethods = { localize, saveChanges }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function loadUsers() {
                const api = new AccountApi

                api.getPartnerList()
                    .then(response => setUsers(response.data))
            }

            function setUsers(partners: IPartnerAccountFullDTO[]) {
                let users: IUserRow[] = []

                for (const partner of partners) {
                    for (const user of partner.users) {
                        users.push({
                            accountNo: partner.accountNo,
                            companyName: partner.companyName,
                            lastActivity: Converter.toLocalDate(user.lastActivityIsoString),
                            lockedOutUntil: Converter.toLocalDate(user.lockedOutUntilIsoString),
                            language: Converter.languageCodeToLanguage(user.languageCode, localize),
                            isPartnerAdmin: user.permissions.isPartnerAdmin,
                            isPriceAccessAllowed: user.permissions.priceAccessAllowed,
                            ...user
                        })
                    }
                }

                allUsers.value = users

                // build snapshot and clear dirty
                const snapshot: Record<TKey, boolean> = {}
                for (const user of allUsers.value) {
                    snapshot[user.email] = user.isPartnerAdmin
                }
                originalData.value = snapshot
                dirtyRows.value.clear()
            }

            // Custom cell render function for column "isLockedOut"
            function getLockedOutCell(render: typeof h, tdElement: any, props: GridCellProps): VNode {
                const componentInstance = render(lockedOutCell, {
                    ...props,
                    onUnlock: onUnlock
                })

                return componentInstance
            }

            // Custom cell render function for column "isPartnerAdmin"
            function getIsPartnerAdminCell(render: typeof h, tdElement: any, props: GridCellProps): VNode {
                const componentInstance = render(checkboxCell, {
                    ...props,
                    onChange: onUpdatePartnerAdmin
                })

                return componentInstance
            }

            async function onUnlock(button: InstanceType<typeof Button> | null, username: string) {
                const api = new AuthenticationApi

                if (button != null) {
                    const target = button.$el as HTMLButtonElement

                    performButtonAction(target, () => api.unlockUser(username))
                        .then(() => {
                            // Reload users
                            allUsers.value = []
                            loadUsers()

                            const message = `${localize('Success_UserUnlocked')}: ${username}`
                            notificationStore.addNotification(new NotificationDTO(message, 'success'))
                        })
                }
            }

            async function onUpdatePartnerAdmin(dataItem: Object, checked: boolean) {
                const user = dataItem as IUserRow
                if (user == null) return;

                const key: TKey = user[keyField]
                user.isPartnerAdmin = checked

                // update dirty set against snapshot
                const wasChecked = originalData.value[key]
                if (wasChecked === checked) {
                    dirtyRows.value.delete(key)
                }
                else {
                    dirtyRows.value.add(key)
                }
            }

            /********************************************************************************************************************
             LIFE-CYCLE HOOKS
             ********************************************************************************************************************/
            onMounted(() => loadUsers())

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
